import { useEffect, useMemo, useState } from "react";
import {
  Check,
  Download,
  Eye,
  FileSpreadsheet,
  Filter,
  RotateCcw,
  UserRound,
  X,
} from "lucide-react";
import "./HRExportingDataPage.css";
import { getApplications } from "../../services/jobService";
import API from "../../services/api";

const escapeCsvValue = (value) => {
  const normalizedValue = value === null || value === undefined ? "" : String(value);
  return `"${normalizedValue.replace(/"/g, '""')}"`;
};

const getTextValue = (...values) => {
  const foundValue = values.find((value) => String(value ?? "").trim());
  return foundValue === undefined ? "-" : String(foundValue).trim();
};

const getCandidateName = (item) => {
  const joinedName = [item.firstName, item.middleName, item.lastName]
    .filter((value) => String(value ?? "").trim())
    .join(" ");
  const nestedJoinedName = [
    item.application?.firstName,
    item.application?.middleName,
    item.application?.lastName,
  ]
    .filter((value) => String(value ?? "").trim())
    .join(" ");

  return getTextValue(
    item.fullName,
    item.candidateName,
    item.name,
    item.application?.fullName,
    item.application?.candidateName,
    joinedName,
    nestedJoinedName,
  );
};

const getCandidatePhone = (item) =>
  getTextValue(
    item.phone,
    item.phoneNumber,
    item.mobile,
    item.mobileNumber,
    item.contactNumber,
    item.secondaryPhone,
    item.application?.phone,
    item.application?.phoneNumber,
    item.application?.mobile,
    item.application?.mobileNumber,
    item.application?.contactNumber,
  );

const timelineStages = [
  {
    key: "APPLIED",
    title: "Applied Job",
    description: "Candidate applied for the job",
    statuses: ["APPLIED"],
  },
  {
    key: "SHORTLISTED",
    title: "Shortlisted",
    description: "Candidate has been shortlisted",
    statuses: ["SHORTLISTED"],
  },
  {
    key: "WRITTEN_TEST",
    title: "Written Test",
    description: "Written test completed",
    statuses: ["WRITTEN_TEST", "WRITTEN_TEST_COMPLETED"],
    roundTypes: ["WRITTEN_TEST"],
  },
  {
    key: "TECHNICAL_ROUND",
    title: "Communication and Technical Round",
    description: "Communication and Technical round completed",
    statuses: ["COMMUNICATION_AND_TECHNICAL_ROUND", "COMMUNICATION_AND_TECHNICAL_ROUND_COMPLETED"],
    roundTypes: [
      "COMMUNICATION_AND_TECHNICAL_INTERVIEW",
      "TECHNICAL_ROUND",
      "TECHNICAL",
    ],
  },
  {
    key: "HR_ROUND",
    title: "HR Round",
    description: "HR round completed",
    statuses: ["HR_ROUND", "HR_ROUND_COMPLETED"],
    roundTypes: ["HR_INTERVIEW", "HR_ROUND", "HR"],
  },
  {
    key: "SELECTED",
    title: "Selected",
    description: "Candidate has been selected",
    statuses: ["SELECTED"],
  },
  {
    key: "OFFER_RELEASED",
    title: "Offer Released",
    description: "Offer letter has been released",
    statuses: ["OFFER_RELEASED"],
  },
  {
    key: "ONBOARDING",
    title: "Onboarding",
    description: "Onboarding is pending",
    statuses: ["ONBOARDING", "ONBOARDED"],
  },
];

const formatTimelineDate = (date) => {
  if (!date || Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("en-GB");
};

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getInterviewStageDate = (candidate, roundTypes = []) => {
  const interviews = Array.isArray(candidate?.interviews) ? candidate.interviews : [];
  const normalizedRoundTypes = roundTypes.map((roundType) => String(roundType).toUpperCase());
  const matchingInterviews = interviews.filter((item) =>
    normalizedRoundTypes.includes(
      String(item.roundType || item.displayRoundType || item.originalRoundType || "").toUpperCase(),
    ),
  );
  const interview =
    matchingInterviews.find((item) => String(item.status || "").toUpperCase() === "COMPLETED") ||
    matchingInterviews[0];

  if (!interview) {
    return null;
  }

  const date = parseDateValue(
    interview.completedAt ||
      interview.rawCompletedAt ||
      interview.updatedAt ||
      interview.rawDate ||
      interview.date ||
      interview.createdAt,
  );

  return date ? { date } : null;
};

const getLatestSelectedInterviewDate = (candidate) => {
  const interviews = Array.isArray(candidate?.interviews) ? candidate.interviews : [];
  const selectedInterviews = interviews
    .filter((item) => String(item.result || "").toUpperCase() === "SELECTED")
    .map((item) =>
      parseDateValue(
        item.completedAt ||
          item.rawCompletedAt ||
          item.updatedAt ||
          item.rawDate ||
          item.date ||
          item.createdAt,
      ),
    )
    .filter(Boolean)
    .sort((left, right) => right.getTime() - left.getTime());

  return selectedInterviews[0] || null;
};

const getStageDateInfo = (candidate, stage) => {
  if (!candidate) {
    return { date: null, time: "-", hasDate: false };
  }

  if (stage.key === "APPLIED") {
    return {
      date: candidate.appliedDate,
      hasDate: Boolean(candidate.appliedDate),
    };
  }

  const normalizedStatus = String(candidate.status || "").toUpperCase();
  const updatedDate = parseDateValue(candidate.updatedAt);

  if (
    stage.key === "SHORTLISTED" &&
    normalizedStatus !== "APPLIED" &&
    normalizedStatus !== "-" &&
    updatedDate
  ) {
    return {
      date: updatedDate,
      hasDate: true,
    };
  }

  if (stage.roundTypes) {
    const interviewDateInfo = getInterviewStageDate(candidate, stage.roundTypes);

    if (interviewDateInfo?.date) {
      return {
        date: interviewDateInfo.date,
        hasDate: true,
      };
    }
  }

  if (stage.key === "SELECTED") {
    const selectedInterviewDate = getLatestSelectedInterviewDate(candidate);

    if (selectedInterviewDate) {
      return {
        date: selectedInterviewDate,
        hasDate: true,
      };
    }
  }

  const isCurrentStatus = stage.statuses.includes(normalizedStatus);

  if (isCurrentStatus && updatedDate) {
    return {
      date: updatedDate,
      hasDate: true,
    };
  }

  return { date: null, time: "-", hasDate: false };
};

const hasInterviewRound = (candidate, roundTypes = []) => {
  const interviews = Array.isArray(candidate?.interviews) ? candidate.interviews : [];
  const normalizedRoundTypes = roundTypes.map((roundType) => String(roundType).toUpperCase());

  return interviews.some((item) =>
    normalizedRoundTypes.includes(
      String(item.roundType || item.displayRoundType || item.originalRoundType || "").toUpperCase(),
    ) &&
    (
      String(item.status || "").toUpperCase() === "COMPLETED" ||
      ["SELECTED", "REJECTED"].includes(String(item.result || "").toUpperCase())
    ),
  );
};

const isStageCompleted = (candidate, stage) => {
  const status = String(candidate?.status || "").toUpperCase();

  if (stage.key === "APPLIED") {
    return Boolean(candidate?.appliedDate);
  }

  if (stage.key === "SHORTLISTED") {
    return status !== "APPLIED" && status !== "-";
  }

  if (stage.roundTypes) {
    return hasInterviewRound(candidate, stage.roundTypes);
  }

  if (stage.key === "SELECTED") {
    return ["SELECTED", "OFFER_RELEASED", "ONBOARDING", "ONBOARDED"].includes(status);
  }

  if (stage.key === "OFFER_RELEASED") {
    return ["OFFER_RELEASED", "ONBOARDING", "ONBOARDED"].includes(status);
  }

  if (stage.key === "ONBOARDING") {
    return ["ONBOARDING", "ONBOARDED"].includes(status);
  }

  return stage.statuses.includes(status);
};

const buildTimeline = (candidate) => {
  return timelineStages.map((stage) => {
    const dateInfo = getStageDateInfo(candidate, stage);
    const isCompleted = isStageCompleted(candidate, stage);

    return {
      ...stage,
      date: dateInfo.date,
      hasDate: dateInfo.hasDate,
      isCompleted,
      isPending: !isCompleted,
    };
  });
};

const getTimelineExportValue = (stage) => {
  if (!stage.isCompleted) {
    return "Pending";
  }

  if (!stage.hasDate) {
    return "Completed - Date not available";
  }

  return `Completed - ${formatTimelineDate(stage.date)}`;
};

const HRExportingDataPage = () => {
  const [statusFilter, setStatusFilter] = useState("All");
  const [jobFilter, setJobFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [applications, setScheduledInterviews] = useState([]);
  const [activeCandidate, setActiveCandidate] = useState(null);

  
  const fetchScheduledInterviewsNew = async () => {
    try {
      const [res, timelineResponse] = await Promise.all([
        getApplications(),
        API.get("/applications/filter"),
      ]);
      const applicationRecords = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];
      const timelineRecords = Array.isArray(timelineResponse?.data?.data)
        ? timelineResponse.data.data
        : [];
      const timelineById = new Map(
        timelineRecords.map((item) => [item.id || item.applicationId, item]),
      );
      const timelineByEmail = new Map(
        timelineRecords
          .filter((item) => item.email)
          .map((item) => [String(item.email).trim().toLowerCase(), item]),
      );

      
      const normalized = applicationRecords.map((item) => {
        const itemId = item.applicationId || item.id;
        const email = getTextValue(item.email, item.candidateEmail, item.application?.email);
        const timelineItem =
          timelineById.get(itemId) ||
          timelineByEmail.get(String(email).trim().toLowerCase()) ||
          {};

        return {
          id: itemId,
          candidateName: getCandidateName(item),
          email,
          phone: getCandidatePhone(item),
          jobTitle: getTextValue(item.jobTitle, item.job?.title, item.application?.jobTitle),
          status: getTextValue(item.status, item.application?.status),
          updatedAt: timelineItem.updatedAt || item.updatedAt,
          interviews: Array.isArray(timelineItem.interviews) ? timelineItem.interviews : [],

        // appliedDate: item.interviewDate
        //   ? new Date(item.interviewDate)
        //   : new Date(), // fallback so filtering works
          appliedDate: item.createdAt
            ? new Date(item.createdAt)
            : null,
        };
      });



      setScheduledInterviews(normalized);
    } catch (error) {
      console.error("Failed to load applications", error);
      setScheduledInterviews([]);
    }
  };

  useEffect(() => {
    fetchScheduledInterviewsNew();
  }, []);
  const jobs = useMemo(
    () => [
      "All",
      ...new Set(
        applications
          .map((a) => a.jobTitle)
          .filter((title) => {
            const normalizedTitle = String(title || "").trim().toLowerCase();
            return normalizedTitle && normalizedTitle !== "archived role";
          }),
      ),
    ],
    [applications],
  );

  const statuses = [
    "All",
    "APPLIED",
    "SHORTLISTED",
    "INTERVIEW_SCHEDULED",
    "SELECTED",
    "REJECTED",
    "OFFER_RELEASED",
  ];

  
  const filtered = applications.filter((item) => {
    const statusOk = statusFilter === "All" || item.status === statusFilter;

    const jobOk = jobFilter === "All" || item.jobTitle === jobFilter;

    const appliedDate = item.appliedDate;

    const fromOk = !fromDate || (appliedDate && appliedDate >= new Date(fromDate));

    const toOk = !toDate || (appliedDate && appliedDate <= new Date(toDate));

    return statusOk && jobOk && fromOk && toOk;
  });

  
  console.log("applications", applications);

  const handleResetFilters = () => {
    setStatusFilter("All");
    setJobFilter("All");
    setFromDate("");
    setToDate("");
  };

  const handleExport = () => {
    const timelineHeaders = timelineStages.map((stage) => stage.title);
    const headers = [
      "Candidate Name",
      "Email ID",
      "Phone Number",
      "Job Title",
      "Job Status",
      ...timelineHeaders,
    ];
    const rows = filtered.map((r) => {
      const timeline = buildTimeline(r);

      return [
        r.candidateName,
        r.email,
        r.phone,
        r.jobTitle,
        r.status,
        ...timeline.map(getTimelineExportValue),
      ];
    });

    const csv = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\r\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "applications.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  const handleTimelineExport = () => {
    if (!activeCandidate) {
      return;
    }

    const timeline = buildTimeline(activeCandidate);
    const headers = ["Candidate Name", "Stage", "Description", "Date", "Status"];
    const rows = timeline.map((stage) => [
      activeCandidate.candidateName,
      stage.title,
      stage.description,
      stage.isCompleted && stage.hasDate ? formatTimelineDate(stage.date) : "-",
      stage.isCompleted ? "Completed" : "Pending",
    ]);
    const csv = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${activeCandidate.candidateName}-timeline.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <article className="hr-page-card export-page-card">
      <div className="hr-page-title">
        <FileSpreadsheet size={20} />
        <h2>Exporting Data</h2>
      </div>

      <div className="export-filters">
        <div className="export-filter">
          <Filter size={16} />
          <select
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
          >
            {jobs.map((job) => (
              <option key={job} value={job}>
                {job}
              </option>
            ))}
          </select>
        </div>
        <div className="export-filter">
          <Filter size={16} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
        <button
          type="button"
          className="reset-btn"
          onClick={handleResetFilters}
        >
          <RotateCcw size={16} />
        </button>
      </div>

      <div className="export-actions">
        <button type="button" className="export-btn" onClick={handleExport}>
          <Download size={16} />
          Export CSV
        </button>
        <span>{filtered.length} records selected</span> 
      </div>
      <div className="export-table">
        <div className="export-head">
          <span>Candidate Name</span>
          <span>Email ID</span>
          <span>Phone Number</span>
          <span>Job Title</span>
          <span>Job Status</span>
          <span>Action</span>
        </div>
        {filtered.map((item) => (
          <div className="export-row" key={item.id}>
            <span data-label="Candidate Name">{item.candidateName}</span>
            <span data-label="Email ID">{item.email}</span>
            <span data-label="Phone Number">{item.phone}</span>
            <span data-label="Job Title">{item.jobTitle}</span>
            <span data-label="Job Status">{item.status}</span>
            <span className="export-row-action" data-label="Action">
              <button type="button" onClick={() => setActiveCandidate(item)}>
                <Eye size={16} />
                <span>View</span>
              </button>
            </span>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="export-empty">No records found.</div>
        )}
      </div>

      {activeCandidate ? (
        <div
          className="candidate-timeline-overlay"
          role="presentation"
          onClick={() => setActiveCandidate(null)}
        >
          <aside
            className="candidate-timeline-panel"
            aria-label="Candidate process timeline"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="timeline-panel-header">
              <div>
                <UserRound size={20} />
                <h3>Candidate Process Timeline</h3>
              </div>
              <button
                type="button"
                className="timeline-close-btn"
                aria-label="Close timeline"
                onClick={() => setActiveCandidate(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="timeline-candidate-card">
              <span className="timeline-avatar">
                <UserRound size={34} />
              </span>
              <div>
                <h4>{activeCandidate.candidateName}</h4>
                <p>{activeCandidate.jobTitle}</p>
                <span>
                  {activeCandidate.email} <b>|</b> {activeCandidate.phone}
                </span>
              </div>
            </div>

            <div className="timeline-list">
              {buildTimeline(activeCandidate).map((stage) => (
                <div
                  className={`timeline-item ${stage.isCompleted ? "completed" : "pending"}`}
                  key={stage.key}
                >
                  <span className="timeline-marker">
                    {stage.isCompleted ? <Check size={15} /> : null}
                  </span>
                  <div className="timeline-content">
                    <div>
                      <strong>{stage.title}</strong>
                      {stage.isCompleted ? (
                        stage.hasDate ? (
                          <time>{formatTimelineDate(stage.date)}</time>
                        ) : (
                          <em>Date not available</em>
                        )
                      ) : (
                        <mark>Pending</mark>
                      )}
                    </div>
                    <p>{stage.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="timeline-panel-footer">
              <button type="button" className="timeline-close-secondary" onClick={() => setActiveCandidate(null)}>
                Close
              </button>
              <button type="button" className="timeline-export-btn" onClick={handleTimelineExport}>
                <Download size={16} />
                <span>Export Timeline CSV</span>
              </button>
            </div>
          </aside>
        </div>
      ) : null}
    </article>
  );
};

export default HRExportingDataPage;
 
